import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CORRECTION_ONLY_EXCEPTIONS,
  EXCEPTION_LABELS,
  EXCEPTION_SEVERITIES,
  EXCEPTION_TYPES,
  fmtDate,
  type ExceptionDTO,
  type ExceptionSeverity,
  type ExceptionType,
  type ReasonCode,
} from '@em/shared';
import { api, invalidateAttendance, useEmployees, useExceptions, usePeriods } from '../../api';
import { CorrectionDialog, type CorrectionPrefill } from '../../components/CorrectionDialog';
import { deadlineText, SEVERITY } from '../../components/format';
import { PromptDialog } from '../../components/PromptDialog';
import { useToast } from '../../components/Toast';
import { Badge, Button, Card, Checkbox, EmptyState, Field, PageHeader, QueryState, Select, SeverityBadge } from '../../components/ui';

export function ExceptionsPage() {
  const [type, setType] = useState<ExceptionType | ''>('');
  const [severity, setSeverity] = useState<ExceptionSeverity | ''>('');
  const [employeeId, setEmployeeId] = useState('');
  const [periodId, setPeriodId] = useState('');
  const [showInfo, setShowInfo] = useState(false);
  const [correcting, setCorrecting] = useState<CorrectionPrefill | null>(null);
  const [dismissing, setDismissing] = useState<ExceptionDTO | null>(null);
  const toast = useToast();

  // The unfiltered open list is shared with the sidebar count; filters narrow it client side.
  const q = useExceptions({ status: 'open' });
  const employees = useEmployees({ status: 'all' });
  const periods = usePeriods();

  const rows = useMemo(() => {
    const list = q.data ?? [];
    return list.filter(
      (x) =>
        (!type || x.type === type) &&
        (!employeeId || x.employeeId === employeeId) &&
        (!periodId || x.payPeriodId === periodId) &&
        (severity ? x.severity === severity : showInfo || x.severity !== 'info'),
    );
  }, [q.data, type, employeeId, periodId, severity, showInfo]);
  const hiddenInfo = (q.data ?? []).filter((x) => x.severity === 'info').length;
  const counts = useMemo(() => {
    const c = { blocking: 0, review: 0, info: 0 };
    for (const x of q.data ?? []) c[x.severity]++;
    return c;
  }, [q.data]);

  const reasonFor = (t: ExceptionType): ReasonCode => (t === 'auto_clock_out' ? 'auto_clock_out' : t === 'duplicate_punch' ? 'duplicate' : t === 'wrong_site' ? 'wrong_site' : t === 'missed_punch' ? 'missed_punch' : 'wrong_time');

  return (
    <>
      <PageHeader
        title="Exceptions"
        subtitle="Open exceptions across your teams, soonest approval deadline first. Blocking exceptions stop a timesheet being approved."
        actions={<Button onClick={() => setCorrecting({})}>New correction</Button>}
      />

      <div className="grid grid-3" style={{ marginBottom: 16 }}>
        {EXCEPTION_SEVERITIES.map((s) => (
          <div key={s} className="card stat">
            <div className="label">{SEVERITY[s].label}</div>
            <div className={`value ${s === 'blocking' ? 'red' : s === 'review' ? 'amber' : ''}`}>{q.data ? counts[s] : '–'}</div>
          </div>
        ))}
      </div>

      <div className="filters">
        <Field label="Type">
          <Select value={type} onChange={(e) => setType(e.target.value as ExceptionType | '')}>
            <option value="">All types</option>
            {EXCEPTION_TYPES.map((t) => (
              <option key={t} value={t}>
                {EXCEPTION_LABELS[t]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Severity">
          <Select value={severity} onChange={(e) => setSeverity(e.target.value as ExceptionSeverity | '')}>
            <option value="">Blocking and review</option>
            {EXCEPTION_SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {SEVERITY[s].label} only
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Employee" className="wide">
          <Select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
            <option value="">Everyone</option>
            {employees.data?.map((e) => (
              <option key={e.id} value={e.id}>
                {e.displayName}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Pay period" className="wide">
          <Select value={periodId} onChange={(e) => setPeriodId(e.target.value)}>
            <option value="">All periods</option>
            {periods.data?.map((p) => (
              <option key={p.id} value={p.id}>
                {fmtDate(p.startDate)} – {fmtDate(p.endDate)}
              </option>
            ))}
          </Select>
        </Field>
        <div style={{ paddingBottom: 7 }}>
          <Checkbox label={`Show info (${hiddenInfo})`} checked={showInfo || severity === 'info'} disabled={!!severity} onChange={setShowInfo} />
        </div>
      </div>

      <Card tight>
        <QueryState q={q} loadingLabel="Loading exceptions">
          {() =>
            rows.length === 0 ? (
              <EmptyState title={q.data?.length ? 'Nothing matches these filters' : 'No open exceptions'}>
                {q.data?.length ? 'Clear a filter to see more.' : 'Every punch is accounted for. Nice work.'}
              </EmptyState>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Severity</th>
                      <th>Details</th>
                      <th>Approval deadline</th>
                      <th>
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((x) => {
                      const dl = deadlineText(x.deadlineDays);
                      const correctionOnly = CORRECTION_ONLY_EXCEPTIONS.includes(x.type);
                      return (
                        <tr key={x.id}>
                          <td className="strong">{x.timesheetId ? <Link to={`/timesheets/${x.timesheetId}`}>{x.employeeName}</Link> : x.employeeName}</td>
                          <td className="nowrap">{fmtDate(x.date)}</td>
                          <td className="nowrap">{EXCEPTION_LABELS[x.type]}</td>
                          <td>
                            <SeverityBadge severity={x.severity} />
                          </td>
                          <td className="wrap">{x.message}</td>
                          <td>
                            <Badge tone={dl.tone}>{dl.text}</Badge>
                          </td>
                          <td className="actions">
                            <Button size="sm" variant="primary" onClick={() => setCorrecting({ employeeId: x.employeeId, date: x.date, eventIds: x.eventIds, reasonCode: reasonFor(x.type) })}>
                              Correct punches
                            </Button>
                            {!correctionOnly && (
                              <Button size="sm" onClick={() => setDismissing(x)}>
                                Dismiss
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          }
        </QueryState>
      </Card>
      {q.data && <p className="small muted" style={{ marginTop: 8 }}>Missed punches, automatic clock-outs and missing pay rules can only be cleared by a correction, so they cannot be dismissed.</p>}

      {correcting && <CorrectionDialog prefill={correcting} onClose={() => setCorrecting(null)} />}
      {dismissing && (
        <PromptDialog
          title="Dismiss exception"
          description={
            <>
              <strong>{dismissing.employeeName}</strong>, {fmtDate(dismissing.date)}: {EXCEPTION_LABELS[dismissing.type]}. <span className="muted">{dismissing.message}</span>
              <p className="small muted" style={{ marginTop: 6 }}>Dismissing accepts the hours as recorded. Your note is kept in the audit log.</p>
            </>
          }
          label="Why is this acceptable?"
          required
          confirmLabel="Dismiss exception"
          onClose={() => setDismissing(null)}
          onConfirm={async (note) => {
            await api.dismissException(dismissing.id, note);
            invalidateAttendance();
            toast.success('Exception dismissed');
          }}
        />
      )}
    </>
  );
}
